/**
 * @module contexts/AppStateProvider
 * @author Charles Blais <charles.blais@canada.ca>
 */

import React, { createContext, useContext, useReducer } from "react";

/**
 * Application state variables
 * ---------------------------
 */
type AppState = {
  geolocationService: string,
  form: {
    codeMap: { [key: string]: string },
    siteDesignations: string[],
    siteDesignationsXVLimit: number[],  // must match user feedback text
    siteDesignationsXSOptions: string[],
    siteDesignationsXSOptionsRange: {
      [key: string]: [number, number],
    },
    latitudeLimits: [number, number],
    longitudeLimits: [number, number],
    initialValues: HazardFormInputs,
  },
  chart: {
    colorTable: string[],
  }
}

type AppStateAction = {
  type: string,
}


export const initialStateProduction: AppState = {
  //hazardService: 'sample.json',
  geolocationService: 'https://geogratis.gc.ca/services/geolocation/en/locate',
  form: {
    codeMap: {
      'nbc2020': 'canSHM6',
      'nbc2025': 'canSHM6P1',
    },
    siteDesignations: ['XV', 'XS'],
    siteDesignationsXVLimit: [140, 3000],
    siteDesignationsXSOptions: ['A', 'B', 'C', 'D', 'E'],
    siteDesignationsXSOptionsRange: {
      A: [1500, 3000],
      B: [760, 1500],
      C: [360, 760],
      D: [180, 360],
      E: [140, 180],
    },
    latitudeLimits: [42, 90],
    longitudeLimits: [-141, -45],
    initialValues: {
      // this must match the value in the codeMap
      code: 'nbc2025',
      siteDesignation: 'XV',
      siteDesignationXV: undefined,
      siteDesignationXS: '',
      latitude: undefined,
      longitude: undefined,
    }
  },
  chart: {
    colorTable: [
      '#B60202',
      '#6A5722',
      '#88027E',
      '#1C6633',
      '#501E1E',
      '#2133B5',
      '#8F430E',
      '#0C624F',
      '#564F13',
      '#5C119C',
      '#6b5777'
    ]
  }
};


const reducer = (
  state: AppState,
  action: AppStateAction,
) => {
  switch (action.type) {
    default:
      return state;
  }
}


/**
 * React useContext hook
 * ---------------------
 */
 type AppContextProps = [
  AppState,
  React.Dispatch<AppStateAction>,
]


export const AppContext = createContext<AppContextProps>([initialStateProduction, () => {}]);

type AppStateProviderProps = {
  initialState: AppState,
  children: React.ReactNode,
}

export const AppStateProvider = ({
  initialState,
  children
}: AppStateProviderProps) => (
  <AppContext.Provider value={useReducer(reducer, initialState)}>
    {children}
  </AppContext.Provider>
);

export const useAppState = () => useContext(AppContext);
